import { useState, useEffect } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Checkbox } from "@/components/ui/checkbox";
import { Card, CardContent } from "@/components/ui/card";
import { trpc } from "@/lib/trpc";
import { Loader2 } from "lucide-react";
import { useLocation } from "wouter";

interface LeadFormProps {
  source?: string;
  variant?: "card" | "inline";
  onSuccess?: () => void;
  prefilledNeighborhood?: string;
  isListingInquiry?: boolean; // If true, sends to both Brennan and Sebastian
}

export default function LeadForm({ 
  source = "website", 
  variant = "card", 
  onSuccess,
  prefilledNeighborhood,
  isListingInquiry = false
}: LeadFormProps) {
  const [, setLocation] = useLocation();
  const [propertyAddress, setPropertyAddress] = useState("");
  const [name, setName] = useState("");
  const [phone, setPhone] = useState("");
  const [email, setEmail] = useState("");
  const [honeypot, setHoneypot] = useState(""); // Hidden spam trap
  const [notRobot, setNotRobot] = useState(false);

  // Prefill address with neighborhood if provided
  useEffect(() => {
    if (prefilledNeighborhood && !propertyAddress) {
      setPropertyAddress(`${prefilledNeighborhood}, Calgary`);
    }
  }, [prefilledNeighborhood]);

  // Use different endpoints based on whether this is a listing inquiry
  const submitRegularLead = trpc.leads.submit.useMutation({
    onSuccess: () => {
      // Track Google Ads conversion
      if (typeof window !== 'undefined' && (window as any).gtag_report_conversion) {
        (window as any).gtag_report_conversion();
      }
      onSuccess?.();
      // Redirect to appropriate Thank You page
      // Landing page sources get neighborhood-specific thank-you
      if (source.startsWith('landing-')) {
        const neighborhood = source.replace('landing-', '');
        setLocation(`/landing-thank-you?neighborhood=${neighborhood}`);
      } else {
        setLocation("/thank-you");
      }
    },
  });

  // Listing leads go to both Brennan AND Sebastian
  const submitListingLead = trpc.listingLeads.submit.useMutation({
    onSuccess: () => {
      // Track Google Ads conversion
      if (typeof window !== 'undefined' && (window as any).gtag_report_conversion) {
        (window as any).gtag_report_conversion();
      }
      onSuccess?.();
      // Redirect to listing-specific Thank You page featuring Sebastian
      if (source.startsWith('landing-')) {
        const neighborhood = source.replace('landing-', '').replace('-listing', '');
        setLocation(`/listing-thank-you?neighborhood=${neighborhood}`);
      } else {
        setLocation("/listing-thank-you");
      }
    },
  });

  // Use the appropriate mutation based on form type
  const submitLead = isListingInquiry ? submitListingLead : submitRegularLead;

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!notRobot) {
      return; // Don't submit if checkbox not checked
    }

    submitLead.mutate({
      propertyAddress,
      name,
      phone,
      email,
      source,
      honeypot, // This should be empty for real users
    });
  };

  const formContent = (
    <form onSubmit={handleSubmit} className="space-y-4">
      {/* Honeypot field - hidden from real users, bots will fill it */}
      <div className="absolute -left-[9999px] opacity-0 pointer-events-none" aria-hidden="true">
        <label htmlFor="website">Website</label>
        <Input
          id="website"
          name="website"
          type="text"
          tabIndex={-1}
          autoComplete="off"
          value={honeypot}
          onChange={(e) => setHoneypot(e.target.value)}
        />
      </div>

      <div className="space-y-2">
        <label htmlFor="propertyAddress" className="text-sm font-medium text-stone-700">
          Property Address *
        </label>
        <Input
          id="propertyAddress"
          autoComplete="street-address"
          placeholder="e.g. 1234 17th Ave SW, Calgary"
          className="h-12 bg-stone-50 border-stone-200 focus:border-[oklch(0.35_0.12_145)] focus:ring-[oklch(0.35_0.12_145)]"
          value={propertyAddress}
          onChange={(e) => setPropertyAddress(e.target.value)}
          required
        />
      </div>

      <div className="space-y-2">
        <label htmlFor="name" className="text-sm font-medium text-stone-700">
          Your Name *
        </label>
        <Input
          id="name"
          autoComplete="name"
          placeholder="John Smith"
          className="h-12 bg-stone-50 border-stone-200 focus:border-[oklch(0.35_0.12_145)] focus:ring-[oklch(0.35_0.12_145)]"
          value={name}
          onChange={(e) => setName(e.target.value)}
          required
        />
      </div>

      <div className="space-y-2">
        <label htmlFor="phone" className="text-sm font-medium text-stone-700">
          Phone Number *
        </label>
        <Input
          id="phone"
          type="tel"
          inputMode="tel"
          autoComplete="tel"
          placeholder="(306) 555-1234"
          className="h-12 bg-stone-50 border-stone-200 focus:border-[oklch(0.35_0.12_145)] focus:ring-[oklch(0.35_0.12_145)]"
          value={phone}
          onChange={(e) => setPhone(e.target.value)}
          required
        />
      </div>

      <div className="space-y-2">
        <label htmlFor="email" className="text-sm font-medium text-stone-700">
          Email Address *
        </label>
        <Input
          id="email"
          type="email"
          autoComplete="email"
          placeholder="john@example.com"
          className="h-12 bg-stone-50 border-stone-200 focus:border-[oklch(0.35_0.12_145)] focus:ring-[oklch(0.35_0.12_145)]"
          value={email}
          onChange={(e) => setEmail(e.target.value)}
          required
        />
      </div>

      {/* Human verification checkbox */}
      <div className="flex items-start space-x-3 py-2">
        <Checkbox
          id="notRobot"
          checked={notRobot}
          onCheckedChange={(checked) => setNotRobot(checked === true)}
          className="mt-1"
        />
        <label
          htmlFor="notRobot"
          className="text-sm text-stone-600 cursor-pointer leading-relaxed"
        >
          I confirm I am not a robot and agree to be contacted about my property.
        </label>
      </div>

      {submitLead.error && (
        <p className="text-sm text-red-600 bg-red-50 p-3 rounded-md">
          {submitLead.error.message?.includes("phone") || submitLead.error.message?.includes("Phone")
            ? "Please enter a valid phone number (e.g., 306-555-1234)"
            : submitLead.error.message?.includes("email") || submitLead.error.message?.includes("Email")
            ? "Please enter a valid email address"
            : submitLead.error.message?.includes("address") || submitLead.error.message?.includes("Address")
            ? "Please enter a valid property address"
            : "Something went wrong. Please try again."}
        </p>
      )}

      <Button
        type="submit"
        disabled={!notRobot || submitLead.isPending}
        className="w-full h-12 text-lg font-bold bg-[oklch(0.75_0.15_85)] hover:bg-[oklch(0.65_0.15_85)] text-[oklch(0.2_0.03_145)] shadow-lg hover:shadow-xl transition-all disabled:opacity-50 disabled:cursor-not-allowed"
      >
        {submitLead.isPending ? (
          <>
            <Loader2 className="w-5 h-5 mr-2 animate-spin" />
            Submitting...
          </>
        ) : (
          "Start the Conversation"
        )}
      </Button>

      <p className="text-xs text-center text-stone-400 pt-2">
        We respect your privacy. Your info is never sold.
      </p>
    </form>
  );

  if (variant === "card") {
    return (
      <Card className="border-0 shadow-2xl bg-white/95 backdrop-blur-sm relative">
        <CardContent className="p-6 md:p-8 space-y-6">
          <div className="space-y-2 text-center">
            <h3 className="text-2xl font-serif font-bold text-stone-900">Let&apos;s Talk</h3>
            <p className="text-stone-600 text-sm">Share a few details and we&apos;ll help you explore your options.</p>
          </div>
          {formContent}
        </CardContent>
      </Card>
    );
  }

  return <div className="relative">{formContent}</div>;
}
